/** Compatibility launcher for scripts installed before inline embeds. */
import React, { useMemo, useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { LAUNCHER_STYLE_CSS } from '../../lib/embed/launcher-styles';
import { matchesAllowedOrigin } from '../../lib/embed/originMatch';
import { validateRoomAccessByHash } from '../../services/roomValidationService';

/**
 * Parse and validate the parent origin from `?embed_origin=...`.
 * Returns the origin string when valid, or `null` otherwise.
 * Accepts only http/https URLs.
 */
function readEmbedOrigin(): string | null {
  if (typeof window === 'undefined') return null;
  const raw = new URLSearchParams(window.location.search).get('embed_origin');
  if (!raw) return null;
  let parsed: URL;
  try {
    parsed = new URL(raw);
  } catch {
    return null;
  }
  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
    return null;
  }
  return parsed.origin;
}

/** Per-campaign allowlist gate state. */
type GateState =
  | { status: 'checking' }
  | { status: 'allowed' }
  | { status: 'blocked' };

const EmbedLauncherPage: React.FC = () => {
  // Resolve the parent origin once on mount: the value is fixed for the
  // life of the iframe and re-parsing on every click would be wasted work.
  const embedOrigin = useMemo(readEmbedOrigin, []);
  const { hash } = useParams<{ hash: string }>();
  const [gate, setGate] = useState<GateState>({ status: 'checking' });

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const access = await validateRoomAccessByHash(hash ?? '');
        if (cancelled) return;
        const allowed = access?.allowed_origins ?? null;
        if (!allowed || allowed.length === 0) {
          setGate({ status: 'allowed' });
          return;
        }
        setGate({
          status: matchesAllowedOrigin(embedOrigin ?? '', allowed)
            ? 'allowed'
            : 'blocked',
        });
      } catch {
        // Fail-open on validation errors: the server still enforces the
        // allowlist on create-response, so a fetch failure here should
        // not permanently break the launcher. Render the button and let the
        // server own the final word.
        if (!cancelled) setGate({ status: 'allowed' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [hash, embedOrigin]);

  const handleStart = useCallback(() => {
    if (!embedOrigin) return; // Refuse to post to an unvalidated origin.
    if (gate.status !== 'allowed') return;
    window.parent.postMessage({ type: 'hollie:launcher_open' }, embedOrigin);
  }, [embedOrigin, gate.status]);

  if (gate.status === 'blocked') {
    return (
      <div
        data-hollie-launcher-root
        data-hollie-launcher-block
        className="relative flex flex-col items-center justify-center w-full h-full bg-background overflow-hidden rounded-2xl text-center px-6"
      >
        <style>{`html,body,#root{height:100%;margin:0;padding:0}body{overflow:hidden}`}</style>
        <h2 className="text-base font-semibold text-foreground lowercase">
          embed not authorized
        </h2>
        <p className="mt-2 text-sm text-muted-foreground lowercase">
          this domain is not on the campaign's allowed list.
        </p>
      </div>
    );
  }

  return (
    <div
      data-hollie-launcher-root
      className="relative flex flex-col items-center justify-center w-full h-full bg-background overflow-hidden rounded-2xl"
    >
      <style>{`html,body,#root{height:100%;margin:0;padding:0}body{overflow:hidden}`}</style>
      <style>{LAUNCHER_STYLE_CSS}</style>
      <button type="button" className="hollie-launcher-btn" onClick={handleStart} disabled={gate.status !== 'allowed'}>Start</button>
    </div>
  );
};

export default EmbedLauncherPage;
